/**
 * Loads plan-cycle context for planning module pages (milestones, assumptions, etc.).
 *
 * Behaviour:
 * - cycleIdParam provided (explicit URL selection) → use that cycle; show a neutral
 *   status pill when non-active (user chose to view this plan deliberately).
 * - cycleIdParam absent (auto-resolved) → resolve first active cycle; when non-active
 *   show an inline switcher so the user can pick a different plan without leaving.
 * - No cycle found at all → NoPlanCycleState (full picker).
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown } from 'lucide-react';
import {
  getFirstActiveCycleProvisional,
  listPlanCycles,
} from '../../lib/planningService';
import type { PlanCycle } from '../../types/planning';
import { NoPlanCycleState } from './NoPlanCycleState';
import { PlanningListSkeleton } from './PlanningListSkeleton';

export interface PlanningCycleContext {
  cycleId: string;
  planVersionId: string;
  isNonActiveCycle: boolean;
}

interface PlanningCycleScopeProps {
  cycleIdParam?: string | null;
  selectedCycleId?: string | null;
  onSelectCycle?: (id: string) => void;
  children: (ctx: PlanningCycleContext) => ReactNode;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  closed: 'Closed',
  superseded: 'Superseded',
};

export function PlanningCycleScope({
  cycleIdParam,
  selectedCycleId,
  onSelectCycle,
  children,
}: PlanningCycleScopeProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState<PlanningCycleContext | null>(null);
  const [cycleStatus, setCycleStatus] = useState<string | null>(null);
  const [allCycles, setAllCycles] = useState<PlanCycle[]>([]);
  const [showSwitcher, setShowSwitcher] = useState(false);

  // Explicit = user navigated to a specific plan; auto-resolved = we picked one for them
  const isExplicit = !!cycleIdParam;

  const load = useCallback(async () => {
    setLoading(true);
    const overrideCycleId = cycleIdParam ?? selectedCycleId ?? undefined;
    const { data } = await getFirstActiveCycleProvisional(
      overrideCycleId ? { cycleId: overrideCycleId } : undefined,
    );
    if (!data) {
      setCtx(null);
      setLoading(false);
      return;
    }
    const planVersionId = data.editingPlanVersionId ?? data.planVersionId ?? '';
    const nonActive = data.isNonActiveCycle ?? false;
    setCtx({ cycleId: data.cycle.id, planVersionId, isNonActiveCycle: nonActive });
    setCycleStatus(data.cycle.status ?? null);
    setLoading(false);

    // Pre-fetch cycles for the inline switcher only when auto-resolved + non-active
    if (!isExplicit && nonActive) {
      listPlanCycles().then(({ data: cycles }) => setAllCycles(cycles ?? []));
    }
  }, [cycleIdParam, selectedCycleId, isExplicit]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <PlanningListSkeleton className="h-full min-h-[520px]" />;

  if (!ctx) {
    return (
      <NoPlanCycleState onSelectCycle={(id) => { onSelectCycle?.(id); }} />
    );
  }

  return (
    <>
      {ctx.isNonActiveCycle && (
        isExplicit ? (
          /* Explicit plan selection: show neutral status, no alarm */
          <div className="flex items-center gap-2 px-3 py-1.5 mb-4 bg-[var(--surface)] border border-[var(--rule)] text-xs text-[var(--text-sec)]">
            <span className="inline-flex items-center gap-1 font-medium">
              <span className={`w-1.5 h-1.5 rounded-full ${
                cycleStatus === 'draft' ? 'bg-[var(--mustard)]'
                : cycleStatus === 'closed' ? 'bg-[var(--slate)]'
                : 'bg-[var(--surface)]'
              }`} />
              {STATUS_LABELS[cycleStatus ?? ''] ?? cycleStatus ?? 'Non-active'} plan
            </span>
            <span className="text-[var(--text-ter)]">—</span>
            <span>Data reflects this plan&apos;s version. Activate from the plan overview when ready.</span>
          </div>
        ) : (
          /* Auto-resolved: amber banner with inline plan switcher */
          <div className="mb-4">
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>No active plan — showing the most recent cycle.</span>
              </div>
              <button
                type="button"
                onClick={() => setShowSwitcher((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-[var(--mustard)] hover:text-[var(--mustard)] whitespace-nowrap"
              >
                Switch plan <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showSwitcher ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {showSwitcher && allCycles.length > 0 && (
              <div className="mt-1 border border-[var(--rule)] bg-[var(--bg-raised)] shadow-sm divide-y divide-[var(--rule)] overflow-hidden">
                {allCycles.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setShowSwitcher(false);
                      // If the cycle is in a plan-specific URL context, navigate; otherwise call the callback
                      const planPath = `/planning/plans/${c.id}`;
                      if (window.location.pathname.includes('/planning/plans/')) {
                        // Replace the cycleId segment in the current path
                        const newPath = window.location.pathname.replace(
                          /\/planning\/plans\/[^/]+/,
                          planPath,
                        );
                        navigate(newPath);
                      } else {
                        onSelectCycle?.(c.id);
                      }
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-[var(--surface)] transition-colors"
                  >
                    <span className="font-medium text-[var(--text-pri)]">{c.name || `Plan ${c.id.slice(0, 8)}`}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      c.status === 'active' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      : c.status === 'draft' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                      : c.status === 'closed' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                    }`}>
                      {c.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      )}
      {children(ctx)}
    </>
  );
}
